import * as $ from "jquery";
import * as _ from "lodash";
import { InstallCollection } from "../../../collections/Installs";
import { ModuleCollection } from "../../../collections/Modules";
import { Intercom } from "../../../common/intercom";
import { Install } from "../../../models/Install";
import { Module, SensorRecord } from "../../../models/Module";
import { GenericDeleteModal } from "../../modals/GenericDeleteModal";
import { MaxValuesModal, MaxValuesOptions } from "../../modals/MaxValuesModal";
import { InstallList, InstallTableProperties } from "../InstallList";
import { BaseAccountListView, BaseAccountViewOptions } from "../_BaseAccountViews";
import { InstallOverviewView } from "./InstallOverviewView";

require("bootstrap-toggle");

interface BridgeCategoriesWithCounts {
    subcategoriesWithCounts: [];
    name: string;
    bridges: {
        onlineCount: number;
        totalCount: number;
    };
    streams: {
        onlineCount: number;
        totalCount: number;
    };
}

interface InstallTotals {
    bridgeCategoriesWithCounts: BridgeCategoriesWithCounts[];
    gateways: {
        list: Module[];
        onlineCount: number;
        totalCount: number;
    };
    modules: {
        onlineCount: number;
        totalCount: number;
    };
    bridges: {
        onlineCount: number;
        totalCount: number;
    };
    streams: {
        onlineCount: number;
        totalCount: number;
    };
    virtualStreams: {
        onlineCount: number;
        totalCount: number;
    };
}

export class InstallAccountView extends BaseAccountListView {
    private moduleCollections: {
        [key: number]: ModuleCollection;
    } = {};
    private moduleDeferreds = {};
    private orgInstallCollection = new InstallCollection(
        this.options.installCollection.filter((i) => {
            return Number(i.get("organization_id")) === this.options.currentOrganizationId && !i.isVirtual();
        })
    );

    private validBridgesTypes = {
        categories: [
            {
                name: "Analog",
                validSensorTypes: [
                    18, // Contact Closure
                    21, // Pulse
                    24, // 4-20mA
                    25, // Thermistor
                    26, // Volt
                    37, // Volt/Thermistor
                    38, // Pulse/Contact Closure
                    // Valid Legacy Sensor Types
                    10, // Power Pulse
                    11, // Submeter kW
                    15, // Water Pulse
                    16, // Submeter KWh
                    19, // Gas Pulse
                ],
            },
            {
                name: "Control",
                validSensorTypes: [
                    29, // Timer Control
                ],
            },
            {
                name: "Environmental",
                validSensorTypes: [
                    1, // Internal Sensors (Temperature and Humidity)
                    34, // Indoor Air Quality
                    35, // Indoor Environmental Quality
                    // Valid Legacy Sensor Types:
                    12, // Air Quality
                    17, // Temperature & Humidity
                    30, // Environmental
                    31, // CO2
                    14, // Light Exposure
                    20, // Ambient Light & Noise
                    28, // Motion
                ],
            },
            {
                name: "Protocol",
                validSensorTypes: [
                    22, // Modbus Master
                    27, // BACnet
                    33, // Water Encoder
                    40, // Dexen Meter
                ],
            },
            {
                name: "Virtual",
                validSensorTypes: [],
            },
        ],
    };

    private installRowTemplate = _.template(require("templates/account/install/installTableRow.html"));
    private installSummaryTemplate = _.template(require("templates/account/install/installTableRowExpanded.html"));

    constructor(options: BaseAccountViewOptions) {
        super(
            <any>_.extend({}, options, {
                tagName: "div",
                events: {
                    "click .delete-from-list": "deleteInstall",
                    "click .set-install-maxes": "setInstallMaxes",
                },
            })
        );
    }

    private async loadCollections() {
        const orgInstalls = this.orgInstallCollection.where({
            organization_id: this.options.currentOrganizationId,
        });

        // Process each installation
        await Promise.all(
            orgInstalls.map(async (install) => {
                const installId = install.get("id");

                // Initialize and fetch module collection for the install
                this.moduleCollections[installId] = new ModuleCollection([], { installId });
                await this.moduleCollections[installId].getCollectionFetchPromise();

                const outerDeferred = $.Deferred();
                this.moduleDeferreds[installId] = outerDeferred;
                outerDeferred.resolve();
            })
        );

        return Promise.resolve(); // Resolve when all installs and their deferreds are processed
    }

    show(orgId, installId?, detail?, subDetail?) {
        // Render install overview view for single install
        if (installId) {
            var view = new InstallOverviewView(_.extend({}, this.options));
            this.$el.empty().append(view.render().$el);
            view.show(installId, detail, subDetail);
            this.subViews.push(view);
            return;
        }

        this.showBreadcrumbs();
    }

    render() {
        this.$el.html(
            this.template({
                organizationCollection: this.options.organizationCollection,
                permissionCollection: this.options.permissionCollection,
                currentOrganizationId: this.options.currentOrganizationId,
                collectionLoaded: true,
                urlAddon: "/installs",
            })
        );

        this.$(".container").append(
            this.panelTemplate({
                panelTitle: {
                    text: "Installations",
                    url: null,
                },
                panelClass: "installs",
                panelWidth: "",
                headerLink: null,
                addButton: null,
                search: true,
                collectionLoaded: true,
                footer: null,
            })
        );

        this.showBreadcrumbs();

        // Display the install page with spinners for summary and install rows
        this.showLoadingTable();

        this.loadCollections()
            .then(() => {
                // Asynchronously process and render rows
                const rowsRendered = this.renderRows();

                // Wait for all rows to be rendered before rendering the summary
                Promise.all(rowsRendered)
                    .then(() => {
                        this.renderSummary();
                    })
                    .catch((error) => {
                        console.error("Error rendering rows or summary:", error);
                    });
            })
            .catch((error) => {
                console.error("Error loading collections:", error);
            });

        this.delegateEvents();
        return this;
    }

    remove() {
        // The per-install action-icon tooltips use data-container="body", so their tip
        // nodes are appended to <body> (outside this.$el) and would be orphaned/stuck when
        // the view is torn down. Destroy all tooltips here while the trigger elements still
        // exist so Bootstrap removes their body-appended tip nodes before super.remove().
        (<any>this.$('[data-toggle="tooltip"]')).tooltip("destroy");
        return super.remove();
    }

    renderRows() {
        var deferreds = [];
        var orgInstalls = this.orgInstallCollection.where({
            organization_id: this.options.currentOrganizationId,
        });
        _.each(orgInstalls, (install: Install) => {
            var installId = install.get("id");

            var rowDeferred = $.Deferred();
            deferreds.push(rowDeferred);

            $.when(this.moduleDeferreds[installId]).done(() => {
                var moduleCollection: ModuleCollection = this.moduleCollections[installId];
                // Get all gateway/node/channel/bridge/stream data for this install
                var totals = this.countModulesBridgesStreams(moduleCollection);

                var installRowSummaryProperties = {
                    permissionCollection: this.options.permissionCollection,
                    install: install,
                    moduleCollection: moduleCollection,
                    totals: totals,
                    getOnlineRatio: this.getOnlineRatio,
                    getOfflineRatio: this.getOfflineRatio,
                    getSensorName: this.getSensorName,
                    canSetMaxValues: this.options.currentUser.isSensewareAdmin(),
                    rowUrl: "#/account/" + this.options.currentOrganizationId + "/installs/",
                    validBridgesTypes: this.validBridgesTypes,
                    isSummary: false,
                };

                this.$(".install-row-wrapper.install-id-" + installId)
                    .empty()
                    .html(this.installRowTemplate(installRowSummaryProperties));
                this.$(".install-row-wrapper.install-id-" + installId + " .panel-body.install-summary")
                    .empty()
                    .html(this.installSummaryTemplate(installRowSummaryProperties));

                (<any>this.$('[data-toggle="tooltip"]')).tooltip();

                rowDeferred.resolve();
            });
        });

        return deferreds;
    }

    private getOnlineRatio(online: number, total: number, max: number) {
        return (online / _.max([max, total])) * 100;
    }

    private getOfflineRatio(online: number, total: number, max: number) {
        return ((total - online) / _.max([max, total])) * 100;
    }

    private getSensorName(sensor) {
        if (sensor.sensor_type === 1) return "Temperature and Humidity Node";
        return sensor.sensor_name;
    }

    /**
     * This function counts the total number of gateways, modules, bridges, and streams from a collection of modules.
     * It categorizes modules, processes sensors for each module, and aggregates online and total counts
     * for gateways, bridges, and streams across different categories.
     *
     * @param {ModuleCollection} installModuleCollection - A collection of modules to process for gateways, bridges, and streams.
     * @returns {InstallTotals} An object containing the counts of gateways, modules, bridges, streams, and detailed category information.
     */
    private countModulesBridgesStreams(installModuleCollection: ModuleCollection): InstallTotals {
        const validModules = this.getValidModules(installModuleCollection);

        // Handle Gateways and Modules
        let gatewayAndModuleCounts = this.processGatewaysAndModules(validModules);

        // Handle Bridges and Streams
        const bridgeCategoriesWithCounts = new Map();
        for (const module of validModules) {
            // Retrieve all sensors from module connectors
            let sensors = this.getAllSensors(module);

            // We use a list of valid sensor types by category, this means that
            // we first filter out any sensor that is not included in the list.
            for (const category of this.validBridgesTypes.categories) {
                // Only look at the sensors that are part of the categories valid sensor types.
                // Special case for virtual, we dont exclude any sensor.
                let categorySensors;
                let totalBridgesCount = 0;
                let onlineBridgesCount = 0;
                if (category.name.toLowerCase() === "virtual") {
                    categorySensors = sensors;
                } else {
                    categorySensors = sensors.filter((sensor) =>
                        category.validSensorTypes.includes(sensor.sensor_type)
                    );

                    // Handle Bridges
                    totalBridgesCount = categorySensors.length;
                    onlineBridgesCount = categorySensors.filter((sensor) =>
                        module.connectorInUse(sensor.sensor_connector)
                    ).length;
                }

                // Handle Streams
                // We apply different rules for counting streams based on the category
                let onlineStreamsCount = 0;
                let totalStreamsCount = 0;
                const subcategoriesWithCounts = new Map();
                const categoryName = category.name;
                switch (categoryName) {
                    case "Analog":
                        // Rules for Analog sensors
                        // 1(one) channel belonging to an analog sensor type counts as 1(one) stream.
                        // Exception: do not count channels that match one of the following default names:
                        const defaultNamesToExclude = [
                            // Pulse| Water Pulse | Gas Pulse
                            "Pulse 1",
                            "Pulse 2",
                            "Pulse 3",
                            "Pulse 4",
                            // Dual Volt/Thermistor
                            "Voltage Input 1",
                            "Voltage Input 2",
                            "Thermistor Input 1",
                            "Thermistor Input 2",
                            // Contact Closure
                            "Contact Closure 1",
                            "Contact Closure 2",
                            "Contact Closure 3",
                            "Contact Closure 4",
                            "Contact Closure 5",
                            "Event Counter 1",
                            "Event Counter 2",
                            "Event Counter 3",
                            "Event Counter 4",
                            // 4-20mA | Thermistor | Volt
                            "Input 1",
                            "Input 2",
                            "Input 3",
                            "Input 4",
                            // Power Pulse
                            "Pulse 1 H",
                            "Pulse 2 H",
                            "Pulse 3 H",
                            "Pulse 4 H",
                            "Pulse 1 L",
                            "Pulse 2 L",
                            "Pulse 3 L",
                            "Pulse 4 L",
                            // Submeter kW | Submeter KWh
                            "Power 1",
                            "Power 2",
                            "Power 3",
                            "Power Factor 1",
                            "Power Factor 2",
                            "Power Factor 3",
                        ];
                        const lowercaseDefaultNamesToExclude = defaultNamesToExclude.map((name) => name.toLowerCase());
                        categorySensors.forEach((sensor) => {
                            const sensorName = this.getSensorName(sensor);
                            if (!subcategoriesWithCounts.has(sensorName)) {
                                subcategoriesWithCounts.set(sensorName, {
                                    name: sensorName,
                                    totalBridgesCount: 0,
                                    onlineBridgesCount: 0,
                                    totalStreamsCount: 0,
                                    onlineStreamsCount: 0,
                                });
                            }
                            const counts = subcategoriesWithCounts.get(sensorName);
                            counts.totalBridgesCount += 1;
                            const isSensorOnline = module.connectorInUse(sensor.sensor_connector);
                            if (isSensorOnline) counts.onlineBridgesCount += 1;

                            sensor.channels.forEach((channel) => {
                                const shouldExclude = lowercaseDefaultNamesToExclude.includes(
                                    channel.channel_name.toLowerCase()
                                );
                                const isVirtual = channel.channel_virtual;
                                if (!shouldExclude && !isVirtual) {
                                    counts.totalStreamsCount += 1;
                                    if (isSensorOnline) counts.onlineStreamsCount += 1;

                                    totalStreamsCount += 1;
                                    if (isSensorOnline) onlineStreamsCount += 1;
                                }
                            });
                        });
                        break;
                    case "Control":
                        // Rules for Control sensors
                        // We count 4(four) streams for every Control bridge
                        categorySensors.forEach((sensor) => {
                            const sensorName = this.getSensorName(sensor);
                            if (!subcategoriesWithCounts.has(sensorName)) {
                                subcategoriesWithCounts.set(sensorName, {
                                    name: sensorName,
                                    totalBridgesCount: 0,
                                    onlineBridgesCount: 0,
                                    totalStreamsCount: 0,
                                    onlineStreamsCount: 0,
                                });
                            }
                            const counts = subcategoriesWithCounts.get(sensorName);
                            counts.totalBridgesCount += 1;
                            counts.totalStreamsCount += 4;
                            totalStreamsCount += 4;
                            const isSensorOnline = module.connectorInUse(sensor.sensor_connector);
                            if (isSensorOnline) {
                                counts.onlineBridgesCount += 1;
                                counts.onlineStreamsCount += 4;
                                onlineStreamsCount += 4;
                            }
                        });
                        break;
                    case "Environmental":
                        // Rules for Environmental sensors
                        // Count every node/module that contains at least one environmental bridge.
                        if (categorySensors.length != 0) {
                            // We want to split the Environmental sensors into two categories
                            // depending on their type:
                            // IAQ/IEQ: it contains at least 1 sensor with type 34 (Indoor Air Quality)
                            // or 35 (Indoor Environmental Quality).
                            // Temperature and Humidity Node: it only contains sensors with type 1 (Internal Sensors).
                            let subcategoryName = "IAQ/IEQ";
                            const allAreType1 = categorySensors.every((sensor) => sensor.sensor_type === 1);
                            if (allAreType1) {
                                subcategoryName = "Temperature and Humidity Node";
                            }
                            if (!subcategoriesWithCounts.has(subcategoryName)) {
                                subcategoriesWithCounts.set(subcategoryName, {
                                    name: subcategoryName,
                                    totalBridgesCount: 0,
                                    onlineBridgesCount: 0,
                                    totalStreamsCount: 0,
                                    onlineStreamsCount: 0,
                                });
                            }
                            const counts = subcategoriesWithCounts.get(subcategoryName);
                            counts.totalBridgesCount += categorySensors.length;
                            counts.totalStreamsCount += 1;
                            totalStreamsCount += 1;
                            if (module.isOnline()) {
                                counts.onlineBridgesCount += categorySensors.length;
                                counts.onlineStreamsCount += 1;
                                onlineStreamsCount += 1;
                            }
                        }
                        break;
                    case "Protocol":
                        // Rules for Protocol sensors
                        // 1(one) channel belonging to a Protocol sensor type counts as 1(one) stream.
                        categorySensors.forEach((sensor) => {
                            const sensorName = this.getSensorName(sensor);
                            if (!subcategoriesWithCounts.has(sensorName)) {
                                subcategoriesWithCounts.set(sensorName, {
                                    name: sensorName,
                                    totalBridgesCount: 0,
                                    onlineBridgesCount: 0,
                                    totalStreamsCount: 0,
                                    onlineStreamsCount: 0,
                                });
                            }

                            // Exclude virtual channels
                            const validChannels = sensor.channels.filter((c) => !c.channel_virtual);
                            const counts = subcategoriesWithCounts.get(sensorName);
                            counts.totalBridgesCount += 1;
                            counts.totalStreamsCount += validChannels.length;
                            totalStreamsCount += validChannels.length;
                            const isSensorOnline = module.connectorInUse(sensor.sensor_connector);
                            if (isSensorOnline) {
                                counts.onlineBridgesCount += 1;
                                counts.onlineStreamsCount += validChannels.length;
                                onlineStreamsCount += validChannels.length;
                            }
                        });
                        break;
                    case "Virtual":
                        // Rules for Virtual Channels
                        // 1(one) virtual channel counts as 1(one) stream
                        categorySensors.forEach((sensor) => {
                            sensor.channels.forEach((channel) => {
                                const isVirtual = channel.channel_virtual;
                                if (isVirtual) {
                                    totalStreamsCount += 1;

                                    const isSensorOnline = module.connectorInUse(sensor.sensor_connector);
                                    if (isSensorOnline) onlineStreamsCount += 1;
                                }
                            });
                        });
                        break;
                }

                // Initialize the category if it doesn't exist
                if (!bridgeCategoriesWithCounts.has(categoryName)) {
                    bridgeCategoriesWithCounts.set(categoryName, {
                        subcategoriesWithCounts: new Map(),
                        name: categoryName,
                        bridges: {
                            onlineCount: 0,
                            totalCount: 0,
                        },
                        streams: {
                            onlineCount: 0,
                            totalCount: 0,
                        },
                    });
                }

                // Get and update the category's current counts
                const categoryData = bridgeCategoriesWithCounts.get(categoryName);
                categoryData.bridges.onlineCount += onlineBridgesCount;
                categoryData.bridges.totalCount += totalBridgesCount;
                categoryData.streams.onlineCount += onlineStreamsCount;
                categoryData.streams.totalCount += totalStreamsCount;

                // Merge subcategory counts
                subcategoriesWithCounts.forEach((counts, subcategoryName) => {
                    if (!categoryData.subcategoriesWithCounts.has(subcategoryName)) {
                        categoryData.subcategoriesWithCounts.set(subcategoryName, counts);
                    } else {
                        const existingCounts = categoryData.subcategoriesWithCounts.get(subcategoryName);
                        existingCounts.totalBridgesCount += counts.totalBridgesCount;
                        existingCounts.onlineBridgesCount += counts.onlineBridgesCount;
                        existingCounts.totalStreamsCount += counts.totalStreamsCount;
                        existingCounts.onlineStreamsCount += counts.onlineStreamsCount;
                    }
                });

                bridgeCategoriesWithCounts.set(categoryName, categoryData);
            }
        }

        // Get totals for bridges and streams
        let onlineBridgesCount = 0;
        let totalBridgesCount = 0;
        let onlineStreamsCount = 0;
        let totalStreamsCount = 0;
        let virtualOnlineStreamsCount = 0;
        let virtualTotalStreamsCount = 0;
        for (let value of bridgeCategoriesWithCounts.values()) {
            value.subcategoriesWithCounts = Array.from(value.subcategoriesWithCounts.values());

            // Don't add virtual streams to overall totals, since we have a separate category for that
            if (value.name === "Virtual") {
                virtualOnlineStreamsCount += value.streams.onlineCount;
                virtualTotalStreamsCount += value.streams.totalCount;
                continue;
            }
            onlineBridgesCount += value.bridges.onlineCount;
            totalBridgesCount += value.bridges.totalCount;
            onlineStreamsCount += value.streams.onlineCount;
            totalStreamsCount += value.streams.totalCount;
        }

        return {
            gateways: {
                list: gatewayAndModuleCounts.gatewaysList,
                onlineCount: gatewayAndModuleCounts.onlineGatewaysCount,
                totalCount: gatewayAndModuleCounts.totalGatewaysCount,
            },
            modules: {
                onlineCount: gatewayAndModuleCounts.onlineModulesCount,
                totalCount: gatewayAndModuleCounts.totalModulesCount,
            },
            bridges: {
                onlineCount: onlineBridgesCount,
                totalCount: totalBridgesCount,
            },
            streams: {
                onlineCount: onlineStreamsCount,
                totalCount: totalStreamsCount,
            },
            virtualStreams: {
                onlineCount: virtualOnlineStreamsCount,
                totalCount: virtualTotalStreamsCount,
            },
            bridgeCategoriesWithCounts: Array.from(bridgeCategoriesWithCounts.values()),
        };
    }

    /**
     * Processes a list of modules to categorize them into gateways and non-gateway modules.
     * It calculates and returns the total and online counts of both gateways and modules.
     *
     * The function iterates over the input list of modules, determines whether each module is a gateway,
     * and updates the respective counts (total and online) for gateways and modules accordingly.
     * If the module is a gateway, it is added to the `gatewaysList`.
     *
     * @param {Module[]} modules - An array of Module objects to be processed.
     *
     * @returns {Object} An object containing:
     *          - gatewaysList: Module[] (Array of modules classified as gateways),
     *          - totalGatewaysCount: number (Total count of gateway modules),
     *          - onlineGatewaysCount: number (Count of gateway modules that are online),
     *          - totalModulesCount: number (Total count of non-gateway modules),
     *          - onlineModulesCount: number (Count of non-gateway modules that are online).
     */
    private processGatewaysAndModules(modules: Module[]): {
        gatewaysList: Module[];
        totalGatewaysCount: number;
        onlineGatewaysCount: number;
        totalModulesCount: number;
        onlineModulesCount: number;
    } {
        let gatewaysList = [];
        let totalGatewaysCount = 0;
        let onlineGatewaysCount = 0;
        let totalModulesCount = 0;
        let onlineModulesCount = 0;

        for (const module of modules) {
            if (module.isGateway()) {
                gatewaysList.push(module);
                totalGatewaysCount += 1;
                if (module.isOnline()) onlineGatewaysCount += 1;
            } else {
                totalModulesCount += 1;
                if (module.isOnline()) onlineModulesCount += 1;
            }
        }

        return { gatewaysList, totalGatewaysCount, onlineGatewaysCount, totalModulesCount, onlineModulesCount };
    }

    /**
     * Filters out invalid modules (virtual or invalid module_number)
     * @param installModuleCollection
     * @returns Valid module list
     */
    private getValidModules(installModuleCollection: ModuleCollection): Module[] {
        return installModuleCollection.filter(
            (module) => !module.isVirtualModule() && Number(module.get("module_number")) >= 1
        );
    }

    /**
     * Retrieves all sensors from a module's connectors
     * @param module
     * @returns Array of sensors from all connectors
     */
    private getAllSensors(module: Module): SensorRecord[] {
        const connectors = [1, 2, 3, 4];
        return connectors.flatMap((connector) => module.getConnector(connector) || []);
    }

    /**
     * Calculates summary totals for gateways, modules, bridges, streams, and virtual streams
     * from a list of InstallTotals objects. It aggregates online and total counts for these
     * entities and processes bridge categories and their respective subcategories to sum their
     * counts as well.
     *
     * The result includes:
     * - Total and online counts for gateways, modules, bridges, streams, and virtual streams.
     * - Bridge categories with summed online and total counts, including nested subcategories.
     *
     * @param {InstallTotals[]} list - An array of InstallTotals objects containing data for
     * gateways, modules, bridges, streams, and categorized bridges.
     *
     * @returns {Object} An object representing the summarized totals.
     */
    private calculateSummarySums(list: InstallTotals[]) {
        const result = {
            gateways: { onlineCount: 0, totalCount: 0 },
            modules: { onlineCount: 0, totalCount: 0 },
            bridges: { onlineCount: 0, totalCount: 0 },
            streams: { onlineCount: 0, totalCount: 0 },
            virtualStreams: { onlineCount: 0, totalCount: 0 },
            bridgeCategoriesWithCounts: [],
        };

        // Helper to find or add a category in the result object
        function getCategoryResult(categoryName) {
            let categoryResult = result.bridgeCategoriesWithCounts.find((cat) => cat.name === categoryName);
            if (!categoryResult) {
                categoryResult = {
                    name: categoryName,
                    bridges: { onlineCount: 0, totalCount: 0 },
                    streams: { onlineCount: 0, totalCount: 0 },
                    subcategoriesWithCounts: [],
                };
                result.bridgeCategoriesWithCounts.push(categoryResult);
            }
            return categoryResult;
        }

        // Helper to find or add a subcategory in a category result
        function getSubcategoryResult(categoryResult, subcategoryName) {
            let subcategoryResult = categoryResult.subcategoriesWithCounts.find((sub) => sub.name === subcategoryName);
            if (!subcategoryResult) {
                subcategoryResult = {
                    name: subcategoryName,
                    totalBridgesCount: 0,
                    onlineBridgesCount: 0,
                    totalStreamsCount: 0,
                    onlineStreamsCount: 0,
                };
                categoryResult.subcategoriesWithCounts.push(subcategoryResult);
            }
            return subcategoryResult;
        }

        // Process each data object in the array
        list.forEach((data) => {
            // Sum up gateways, modules, bridges, and streams for each object
            ["gateways", "modules", "bridges", "streams", "virtualStreams"].forEach((key) => {
                result[key].onlineCount += data[key].onlineCount;
                result[key].totalCount += data[key].totalCount;
            });

            // Process bridgeCategoriesWithCounts for each object
            data.bridgeCategoriesWithCounts.forEach((category) => {
                const categoryResult = getCategoryResult(category.name);

                // Sum the counts for the category itself
                categoryResult.bridges.onlineCount += category.bridges.onlineCount;
                categoryResult.bridges.totalCount += category.bridges.totalCount;
                categoryResult.streams.onlineCount += category.streams.onlineCount;
                categoryResult.streams.totalCount += category.streams.totalCount;

                // Process subcategories
                category.subcategoriesWithCounts.forEach((subcat: any) => {
                    const subcategoryResult = getSubcategoryResult(categoryResult, subcat.name);

                    subcategoryResult.totalBridgesCount += subcat.totalBridgesCount;
                    subcategoryResult.onlineBridgesCount += subcat.onlineBridgesCount;
                    subcategoryResult.totalStreamsCount += subcat.totalStreamsCount;
                    subcategoryResult.onlineStreamsCount += subcat.onlineStreamsCount;
                });
            });
        });

        return result;
    }

    renderSummary() {
        const installTotalsList = [];
        this.orgInstallCollection.each((install: Install) => {
            const moduleCollection: ModuleCollection = this.moduleCollections[install.get("id")];
            const installTotals = this.countModulesBridgesStreams(moduleCollection);
            installTotalsList.push(installTotals);
        });

        // Aggregate counts across all installs
        const summaryTotals = this.calculateSummarySums(installTotalsList);

        const totalModules = {};
        const installRowSummaryProperties = {
            permissionCollection: this.options.permissionCollection,
            install: this.currentOrganization,
            // botched module collection is sum of all installs
            moduleCollection: new ModuleCollection(_.values(totalModules), {
                installId: null,
            }),
            totals: summaryTotals,
            getOnlineRatio: this.getOnlineRatio,
            getOfflineRatio: this.getOfflineRatio,
            getSensorName: this.getSensorName,
            canSetMaxValues: this.options.currentUser.isSensewareAdmin(),
            rowUrl: "#/account/" + this.options.currentOrganizationId + "/installs/",
            isSummary: true,
        };

        this.$(".install-summary-wrapper").empty().html(this.installSummaryTemplate(installRowSummaryProperties));
    }

    showLoadingTable() {
        var installTable = new InstallList(
            _.extend(this.options, {
                moduleCollections: this.moduleCollections,
                orgId: this.options.currentOrganizationId,
            })
        );
        var installTableProperties: InstallTableProperties;
        installTableProperties = {
            canDeletePermissionFunction: this.options.permissionCollection.canDeleteInstall.bind(
                this.options.permissionCollection
            ),
            canDeletePermissionFunctionAttribute: "id",
            rowUrl: "#/account/" + this.options.currentOrganizationId + "/installs/",
            tableEmptyText: "You do not have any Installations",
            columnProperties: {
                Install: {
                    columnSizes: {
                        sm: "2",
                        md: "2",
                        xs: "12",
                    },
                },
                Address: {
                    columnSizes: {
                        sm: "2",
                        md: "2",
                        xs: "12",
                    },
                },
                Gateway: {
                    centerColumn: true,
                    columnSizes: {
                        sm: "2",
                        md: "2",
                        xs: "12",
                    },
                },
                Nodes: {
                    centerColumn: true,
                    columnSizes: {
                        sm: "2",
                        md: "2",
                        xs: "12",
                    },
                },
                Bridges: {
                    centerColumn: true,
                    columnSizes: {
                        sm: "2",
                        md: "2",
                        xs: "12",
                    },
                },
                Streams: {
                    centerColumn: true,
                    columnSizes: {
                        sm: "2",
                        md: "2",
                        xs: "12",
                    },
                },
            },
        };
        this.$(".installs .panel-body > .row").append(
            installTable.renderCollectionList(this.orgInstallCollection, installTableProperties).$el
        );
    }

    showBreadcrumbs() {
        super.showBreadcrumbs("Installations", "Categories");
    }

    setInstallMaxes(e: JQuery.UIEventBase) {
        e.stopPropagation();
        var id = Number($(e.currentTarget).attr("data-model-id"));
        var install = this.orgInstallCollection.findWhere({
            id: id,
        });
        var name = _.escape(install.get("name"));

        var modal = new MaxValuesModal({
            titleText: "Update Install " + name,
            text: "For this install, Attune allows the following maximums:",
            name: name,
            record: install,
            buttonText: "Update",
            func: (installOptions: MaxValuesOptions) => {
                return $.ajax({
                    type: "PUT",
                    url:
                        this.options.installCollection.url() +
                        "/" +
                        id +
                        "/updateMaxValues/" +
                        install.get("organization_install_id"),
                    data: JSON.stringify(installOptions),
                    dataType: "json",
                    contentType: "application/json; charset=utf-8",
                    complete: (res) => {
                        // Update our install values in the UI
                        $.when(this.orgInstallCollection.getCollectionFetchPromise()).done(() => {
                            this.reloadUrl();
                        });
                    },
                });
            },
        });
        $(".modals").append(modal.render().el);
        modal.show();
    }

    deleteInstall(e: JQuery.UIEventBase) {
        e.stopPropagation();
        var id = Number($(e.currentTarget).attr("data-model-id"));
        var install = this.options.installCollection.findWhere({
            id: id,
        });

        var modal = new GenericDeleteModal({
            type: "Install",
            name: install.getDisplayName(),
            func: () => {
                return $.ajax({
                    type: "DELETE",
                    url:
                        this.options.organizationCollection.url() +
                        "/" +
                        this.options.currentOrganizationId +
                        "/installs/" +
                        id,
                    dataType: "json",
                    contentType: "application/json; charset=utf-8",
                    complete: () => {
                        Intercom.trackEvent("deleted-install-from-org", {
                            org_id: this.options.currentOrganizationId,
                            user_id: id,
                        });

                        $.when(
                            this.options.installCollection.getCollectionFetchPromise()
                            // this.options.dashboardCollection.getCollectionFetchPromise(),
                            // this.options.dashboardChannelCollection.getCollectionFetchPromise()
                        ).done(() => {
                            this.reloadUrl();
                        });
                    },
                });
            },
        });
        $(".modals").append(modal.render().el);
        modal.show();
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/account/installs/InstallAccountView.ts